import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Presentation, TrendingUp, Wallet, Calendar } from 'lucide-react';
import {useNavigate} from "react-router-dom";

const Services = () => {
    const navigate = useNavigate();
    return (
        <section
            id="services"
            className="
                relative w-full
                /* Padding Logic: Smaller on mobile, standard on tablet, large on desktop */
                py-16 sm:py-20 lg:py-24
                px-4 sm:px-8 lg:px-12 xl:px-20
                overflow-hidden font-poppins
            "
        >

            <div
                className="
                    relative z-10 mx-auto
                    /*
                       WIDTH LOGIC (Responsive):
                       - XS/SM: Full width minus padding
                       - MD (Tablet): 90% (Fixes iPad Pro squashing)
                       - LG (Laptop): 85%
                       - XL (Desktop): 75% + Max Width Cap
                    */
                    w-full sm:w-[92%] md:w-[90%] lg:w-[85%] xl:w-[75%] max-w-7xl
                "
            >

                {/* --- HEADER --- */}
                <div className="text-center mb-12 sm:mb-16 lg:mb-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                        <span className="text-xs font-bold tracking-widest text-primary uppercase">
                            What We Offer
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, ease: 'easeOut' }}
                        /* Typography: Scaled for XS phones up to Desktop */
                        className="text-3xl sm:text-4xl md:text-5xl font-bold text-dark dark:text-light tracking-tight"
                    >
                        Our Premium <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Services</span>
                    </motion.h2>
                </div>

                {/* --- GRID --- */}
                <motion.div
                    /*
                       GRID LOGIC:
                       - 1 Column: Mobile & Small Tablets
                       - 2 Columns: MD (iPad) and up
                    */
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 xl:gap-10 mb-12"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={{
                        hidden: {},
                        visible: { transition: { staggerChildren: 0.15 } },
                    }}
                >
                    <ServiceCard
                        number="01"
                        icon={<Presentation className="w-6 h-6 text-white" />}
                        title="Live Mentorship Programs"
                        desc="Learn directly from experts in real-time trading sessions. Master the charts with live guidance and take your skills to the institutional level."
                        link="#services"
                    />

                    <ServiceCard
                        number="02"
                        icon={<TrendingUp className="w-6 h-6 text-white" />}
                        title="Time Trading Technique"
                        desc="Exclusive access to our proprietary Time-based strategies. Gain valuable insights into calculating market turns before they happen."
                        link="#services"
                    />

                    <ServiceCard
                        number="03"
                        icon={<Wallet className="w-6 h-6 text-white" />}
                        title="Financial Planning"
                        desc="Strategically set goals, manage budgets, and invest wisely. We help you create a roadmap to secure your financial future and retirement."
                        link="#services"
                    />

                    <ServiceCard
                        number="04"
                        icon={<Calendar className="w-6 h-6 text-white" />}
                        title="Fortune Signals Live Events"
                        desc="Attend our high-impact live seminars and networking events. Deep dive into advanced trading techniques and market cycle analysis."
                        link="#services"
                    />
                </motion.div>

                {/* --- Bottom CTA --- */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex justify-center"
                >
                    <button
                        className="
                            group relative px-7 py-3 rounded-full
                            bg-primary text-white
                            font-semibold shadow-lg text-sm
                            hover:shadow-xl hover:-translate-y-1
                            transition-all duration-300
                            flex items-center gap-2
                        "
                        onClick={() => navigate("/courses")}
                    >
                        Explore Services
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
};

export default Services;

/* ------------------------------------------------------------ */
/*                 PROFESSIONAL SERVICE CARD                    */
/* ------------------------------------------------------------ */

function ServiceCard({ number, icon, title, desc, link }) {
    return (
        <motion.div
            /*
               CARD PADDING:
               - p-6 for Mobile (XS/SM) to save space
               - p-8 for Desktop (MD+) for aesthetics
            */
            className="group relative p-6 sm:p-8 rounded-3xl bg-white dark:bg-bg-dark-2 border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 overflow-hidden flex flex-col h-full"
            variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: "easeOut" }
                },
            }}
        >
            {/* Hover Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

            <div className="relative z-10 flex flex-col h-full">

                {/* Header: Icon & Number */}
                <div className="flex justify-between items-start mb-6">
                    {/* Icon Box */}
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 shrink-0">
                        {icon}
                    </div>
                    {/* Number Watermark */}
                    <span className="text-3xl sm:text-4xl font-bold text-gray-100 dark:text-white/5 group-hover:text-primary/10 transition-colors duration-300">
                        {number}
                    </span>
                </div>

                {/* Content */}
                <h3 className="text-xl md:text-2xl font-bold text-dark dark:text-light mb-3 group-hover:text-primary transition-colors duration-300">
                    {title}
                </h3>

                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4 flex-grow">
                    {desc}
                </p>
            </div>

        </motion.div>
    );
}